import React, { useState } from 'react';
import V2Shell from './V2Shell';

// The order system guide from hsy (EN/FR), merged into Replai.
const Card = ({ className = '', children }) => <div className={`rounded-lg border border-[var(--line)] bg-[var(--surface)] ${className}`}>{children}</div>;
const CardHeader = ({ className = '', children }) => <div className={`px-4 ${className}`}>{children}</div>;
const CardTitle = ({ className = '', children }) => <div className={`font-semibold ${className}`}>{children}</div>;
const CardContent = ({ className = '', children }) => <div className={`px-4 pb-4 ${className}`}>{children}</div>;
const Button = ({ variant, size, className = '', ...p }) => <button type="button" {...p} className={`rounded-md ${variant === 'default' ? 'bg-[var(--ink)] text-white' : 'text-[var(--ink-2)] hover:bg-[var(--line-2)]'} ${className}`} />;

const t = (lang, en, fr) => (lang === 'fr' ? fr : en);

const Section = ({ title, children, defaultOpen = false }) => (
  <details open={defaultOpen} className="group">
    <summary className="cursor-pointer select-none list-none">
      <Card className="transition-colors hover:bg-[var(--paper)]">
        <CardHeader className="py-4">
          <CardTitle className="text-base flex items-center justify-between">
            {title}
            <span className="text-[var(--ink-3)] text-xs font-normal group-open:rotate-90 transition-transform">&#9654;</span>
          </CardTitle>
        </CardHeader>
      </Card>
    </summary>
    <div className="mt-2 mb-4 ml-1 pl-4 border-l-2 border-[var(--line)]">
      {children}
    </div>
  </details>
);

const StatusBadge = ({ status, color }) => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${color}`}>
    {status}
  </span>
);

const Table = ({ headers, rows }) => (
  <div className="overflow-x-auto mt-3 mb-4">
    <table className="w-full text-sm border border-[var(--line)] rounded-lg overflow-hidden">
      <thead>
        <tr className="bg-[var(--paper)]">
          {headers.map((h, i) => (
            <th key={i} className="text-left font-medium px-3 py-2 text-xs text-[var(--ink-3)] border-b border-[var(--line)]">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-[var(--line)] last:border-0">
            {row.map((cell, j) => (
              <td key={j} className="px-3 py-2 text-[var(--ink)]">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Tech = ({ children }) => (
  <code className="px-1.5 py-0.5 rounded bg-[var(--line-2)] text-xs font-mono text-[var(--ink)]">{children}</code>
);

const TechNote = ({ lang, children }) => (
  <div className="mt-3 p-3 rounded-lg bg-[var(--paper)] border border-[var(--line)]">
    <p className="text-[10px] font-medium text-[var(--ink-3)] uppercase tracking-wider mb-1.5">
      {t(lang, 'Technical details', 'Details techniques')}
    </p>
    <div className="text-xs text-[var(--ink-3)]">{children}</div>
  </div>
);

const OrderDocs = () => {
  const [lang, setLang] = useState('en');

  return (
    <V2Shell><div className="px-6 py-10 sm:px-10 xl:px-14">
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-4">
        {/* Header + language toggle */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-[var(--ink)]">
              {t(lang, 'Order System Guide', 'Guide du Systeme de Commandes')}
            </h1>
            <p className="text-sm text-[var(--ink-3)] mt-1">
              {t(lang,
                'Everything you need to know about order statuses, alerts, and how the system works.',
                'Tout ce que vous devez savoir sur les statuts de commande, les alertes et le fonctionnement du systeme.'
              )}
            </p>
          </div>
          <div className="flex gap-1 p-0.5 rounded-lg bg-[var(--line-2)] border border-[var(--line)]">
            <Button
              size="sm"
              variant={lang === 'en' ? 'default' : 'ghost'}
              onClick={() => setLang('en')}
              className="text-xs px-3 h-7"
            >
              English
            </Button>
            <Button
              size="sm"
              variant={lang === 'fr' ? 'default' : 'ghost'}
              onClick={() => setLang('fr')}
              className="text-xs px-3 h-7"
            >
              Francais
            </Button>
          </div>
        </div>

        {/* 1. Item Statuses */}
        <Section title={t(lang, 'Item Statuses (per product line)', 'Statuts des articles (par ligne produit)')} defaultOpen>
          <p className="text-sm text-[var(--ink-3)] mb-3">
            {t(lang,
              'Each product in an order has its own status. Here\'s what each one means:',
              'Chaque produit dans une commande a son propre statut. Voici ce que chacun signifie :'
            )}
          </p>
          <Table
            headers={[
              t(lang, 'Status', 'Statut'),
              '',
              t(lang, 'What it means', 'Ce que ca veut dire'),
            ]}
            rows={[
              [
                <StatusBadge status="Item shipped" color="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" />,
                '🟢',
                t(lang,
                  'The item has been shipped from the warehouse to the customer.',
                  'L\'article a ete expedie de l\'entrepot vers le client.'
                ),
              ],
              [
                <StatusBadge status="Item on dropship" color="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" />,
                '🟢',
                t(lang,
                  'The supplier ships this item directly to the customer (we don\'t handle it in our warehouse).',
                  'Le fournisseur expedie cet article directement au client (il ne passe pas par notre entrepot).'
                ),
              ],
              [
                <StatusBadge status="Item available in warehouse" color="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" />,
                '🔵',
                t(lang,
                  'The item is in our warehouse and ready to be packed and shipped.',
                  'L\'article est dans notre entrepot et pret a etre emballe et expedie.'
                ),
              ],
              [
                <StatusBadge status="Item ordered from brand" color="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" />,
                '🟡',
                t(lang,
                  'We\'ve ordered this item from the supplier/brand but haven\'t received it yet.',
                  'Nous avons commande cet article au fournisseur/marque mais ne l\'avons pas encore recu.'
                ),
              ],
              [
                <StatusBadge status="Item later than expected" color="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" />,
                '🟠',
                t(lang,
                  'The item is running late — the expected delivery date has passed and it hasn\'t shipped yet.',
                  'L\'article est en retard — la date de livraison prevue est passee et il n\'a pas encore ete expedie.'
                ),
              ],
              [
                <StatusBadge status="Order placed" color="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300" />,
                '🔮',
                t(lang,
                  'The order has been received but nothing has happened yet in the warehouse.',
                  'La commande a ete recue mais rien ne s\'est encore passe en entrepot.'
                ),
              ],
              [
                <StatusBadge status="Canceled" color="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" />,
                '🔴',
                t(lang,
                  'This item has been cancelled.',
                  'Cet article a ete annule.'
                ),
              ],
            ]}
          />
          <TechNote lang={lang}>
            {t(lang,
              'Statuses are computed automatically from Odoo stock moves and picking data by the data pipeline.',
              'Les statuts sont calcules automatiquement a partir des mouvements de stock et des pickings Odoo par le pipeline de donnees.'
            )}
          </TechNote>
        </Section>

        {/* 2. Order Statuses */}
        <Section title={t(lang, 'Order Statuses (overall)', 'Statuts de commande (global)')}>
          <p className="text-sm text-[var(--ink-3)] mb-3">
            {t(lang,
              'The overall order status is based on all the items in the order (cancelled items are ignored):',
              'Le statut global de la commande est base sur tous les articles de la commande (les articles annules sont ignores) :'
            )}
          </p>
          <Table
            headers={[
              t(lang, 'Status', 'Statut'),
              '',
              t(lang, 'When does an order get this status?', 'Quand une commande obtient-elle ce statut ?'),
            ]}
            rows={[
              [
                <StatusBadge status="Shipped" color="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300" />,
                '🟢',
                t(lang,
                  'All items have been shipped or are on dropship. The order is complete.',
                  'Tous les articles ont ete expedies ou sont en dropship. La commande est terminee.'
                ),
              ],
              [
                <StatusBadge status="Ready in warehouse" color="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" />,
                '🔵',
                t(lang,
                  'All items are either shipped, on dropship, or available in the warehouse. Ready to go!',
                  'Tous les articles sont expedies, en dropship, ou disponibles en entrepot. Pret a partir !'
                ),
              ],
              [
                <StatusBadge status="Ordered from brand" color="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" />,
                '🟡',
                t(lang,
                  'At least one item is still being ordered from the supplier or is running late.',
                  'Au moins un article est encore en commande chez le fournisseur ou est en retard.'
                ),
              ],
              [
                <StatusBadge status="Order placed" color="bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300" />,
                '🔮',
                t(lang,
                  'The order was just placed, items haven\'t started moving yet.',
                  'La commande vient d\'etre passee, les articles n\'ont pas encore commence a bouger.'
                ),
              ],
            ]}
          />
        </Section>

        {/* 3. Dashboard Alerts */}
        <Section title={t(lang, 'Dashboard Alerts — When do orders show up?', 'Alertes du Dashboard — Quand les commandes apparaissent-elles ?')}>
          <p className="text-sm text-[var(--ink-3)] mb-3">
            {t(lang,
              'The Order Dashboard highlights orders that may need your attention. Here are the different alert types:',
              'Le Dashboard Commandes met en avant les commandes qui pourraient necessiter votre attention. Voici les differents types d\'alertes :'
            )}
          </p>

          <div className="space-y-4">
            <div className="p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10">
              <h4 className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">
                {t(lang, 'Overdue Delivery', 'Livraison en retard')}
              </h4>
              <p className="text-sm text-[var(--ink)]">
                {t(lang,
                  'The expected delivery date has passed but the item hasn\'t been shipped. These orders need to be checked — the customer may be waiting.',
                  'La date de livraison prevue est passee mais l\'article n\'a pas ete expedie. Ces commandes doivent etre verifiees — le client attend peut-etre.'
                )}
              </p>
            </div>

            <div className="p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10">
              <h4 className="text-sm font-medium text-amber-700 dark:text-amber-400 mb-1">
                {t(lang, 'Waiting on Brand (>14 days)', 'En attente de la marque (>14 jours)')}
              </h4>
              <p className="text-sm text-[var(--ink)]">
                {t(lang,
                  'The order was placed more than 14 days ago and items are still marked as "Ordered from brand". The supplier may need a follow-up.',
                  'La commande a ete passee il y a plus de 14 jours et les articles sont toujours marques "Commande chez la marque". Le fournisseur a peut-etre besoin d\'un suivi.'
                )}
              </p>
            </div>

            <div className="p-3 rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-900/10">
              <h4 className="text-sm font-medium text-orange-700 dark:text-orange-400 mb-1">
                {t(lang, 'Later Than Expected', 'Plus tard que prevu')}
              </h4>
              <p className="text-sm text-[var(--ink)]">
                {t(lang,
                  'Items flagged by the system as running behind the original expected date.',
                  'Articles signales par le systeme comme etant en retard par rapport a la date prevue initiale.'
                )}
              </p>
            </div>
          </div>

          <h4 className="text-sm font-medium text-[var(--ink)] mt-5 mb-2">
            {t(lang, 'Good to know', 'Bon a savoir')}
          </h4>
          <ul className="list-disc list-inside text-sm text-[var(--ink)] space-y-1.5">
            <li>
              {t(lang,
                'Orders already marked as "Shipped" or "Canceled" never appear in alerts — they\'re done.',
                'Les commandes marquees "Shipped" ou "Canceled" n\'apparaissent jamais dans les alertes — elles sont terminees.'
              )}
            </li>
            <li>
              {t(lang,
                'By default, the dashboard only shows orders from 2025 onwards. You can change this with the date filter.',
                'Par defaut, le dashboard n\'affiche que les commandes a partir de 2025. Vous pouvez changer cela avec le filtre de dates.'
              )}
            </li>
            <li>
              {t(lang,
                'Click any order in the table to see its full details in Order Lookup. Click the external link icon to open it directly in Odoo.',
                'Cliquez sur une commande dans le tableau pour voir tous ses details. Cliquez sur l\'icone de lien externe pour l\'ouvrir directement dans Odoo.'
              )}
            </li>
          </ul>

          <TechNote lang={lang}>
            {t(lang,
              <>Overdue: <Tech>d_expected_delivery &lt; today AND d_shipped IS NULL AND l_status NOT IN (Shipped, Canceled)</Tech>.
              Waiting on brand: <Tech>l_status = &apos;Ordered from brand&apos; AND order_date &lt; today - 14 days</Tech>.
              Dashboard defaults to <Tech>order_date &gt;= 2025-01-01</Tech>. Max 50 problematic orders shown per category.</>,
              <>En retard : <Tech>d_expected_delivery &lt; aujourd&apos;hui AND d_shipped IS NULL AND l_status NOT IN (Shipped, Canceled)</Tech>.
              Attente marque : <Tech>l_status = &apos;Ordered from brand&apos; AND order_date &lt; aujourd&apos;hui - 14 jours</Tech>.
              Dashboard par defaut : <Tech>order_date &gt;= 2025-01-01</Tech>. Max 50 commandes problematiques affichees par categorie.</>
            )}
          </TechNote>
        </Section>

        {/* 4. Cleanup Tab */}
        <Section title={t(lang, 'Cleanup Tab (pre-2025)', 'Onglet Nettoyage (avant 2025)')}>
          <p className="text-sm text-[var(--ink)] mb-3">
            {t(lang,
              'The "Cleanup" tab in the dashboard shows old orders from before 2025 that are still open (not shipped or canceled). These usually need to be closed or cancelled in Odoo.',
              'L\'onglet "Cleanup" dans le dashboard affiche les anciennes commandes d\'avant 2025 qui sont encore ouvertes (ni expediees ni annulees). Celles-ci doivent generalement etre fermees ou annulees dans Odoo.'
            )}
          </p>
          <ul className="list-disc list-inside text-sm text-[var(--ink)] space-y-1.5">
            <li>
              {t(lang,
                'These are often old quotations that were never confirmed, or orders that got stuck.',
                'Ce sont souvent des anciens devis jamais confirmes, ou des commandes qui se sont bloquees.'
              )}
            </li>
            <li>
              {t(lang,
                'Click the Odoo link next to the order name to open it in Odoo and take action.',
                'Cliquez sur le lien Odoo a cote du nom de la commande pour l\'ouvrir dans Odoo et agir.'
              )}
            </li>
          </ul>
        </Section>

        {/* 5. Payment Status */}
        <Section title={t(lang, 'Payment Status', 'Statut de paiement')}>
          <p className="text-sm text-[var(--ink)] mb-3">
            {t(lang,
              'Payment status comes from Odoo invoices, with Shopify as a backup for Shopify orders.',
              'Le statut de paiement vient des factures Odoo, avec Shopify en backup pour les commandes Shopify.'
            )}
          </p>
          <div className="p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 mb-3">
            <p className="text-sm text-[var(--ink)]">
              <strong>{t(lang, 'Known issue:', 'Probleme connu :')}</strong>{' '}
              {t(lang,
                'Some Odoo orders show "Not Paid" even when they are actually paid. This is an Odoo quirk. For Shopify orders, the system automatically uses Shopify\'s payment status instead. Because of this, the payment status badge is currently hidden in the app.',
                'Certaines commandes Odoo affichent "Non paye" meme quand elles sont reellement payees. C\'est un bug Odoo. Pour les commandes Shopify, le systeme utilise automatiquement le statut de paiement Shopify. A cause de ca, le badge de paiement est actuellement cache dans l\'appli.'
              )}
            </p>
          </div>
        </Section>

        {/* 6. Order Name Prefixes */}
        <Section title={t(lang, 'Order Name Prefixes', 'Prefixes des noms de commande')}>
          <p className="text-sm text-[var(--ink-3)] mb-3">
            {t(lang,
              'You can tell where an order came from by its name:',
              'Vous pouvez savoir d\'ou vient une commande par son nom :'
            )}
          </p>
          <Table
            headers={[
              t(lang, 'Prefix', 'Prefixe'),
              t(lang, 'Source', 'Source'),
              t(lang, 'Example', 'Exemple'),
            ]}
            rows={[
              [
                <span className="font-mono font-bold text-[var(--ink)]">#</span>,
                t(lang, 'Shopify orders (webshop)', 'Commandes Shopify (boutique en ligne)'),
                '#152079',
              ],
              [
                <span className="font-mono font-bold text-[var(--ink)]">S</span>,
                t(lang, 'Odoo-native orders (manual/B2B)', 'Commandes natives Odoo (manuel/B2B)'),
                'S45016',
              ],
            ]}
          />
          <p className="text-xs text-[var(--ink-3)]">
            {t(lang,
              'You can search for orders using the full name (#152079) or just the number (152079) in Order Lookup.',
              'Vous pouvez chercher des commandes avec le nom complet (#152079) ou juste le numero (152079) dans Order Lookup.'
            )}
          </p>
        </Section>

        {/* 7. Odoo Link */}
        <Section title={t(lang, 'Opening Orders in Odoo', 'Ouvrir des commandes dans Odoo')}>
          <p className="text-sm text-[var(--ink)] mb-3">
            {t(lang,
              'Every order in the app has a small external link icon next to its name. Clicking it opens the order directly in Odoo so you can take action (update, cancel, check details).',
              'Chaque commande dans l\'appli a une petite icone de lien externe a cote de son nom. En cliquant dessus, la commande s\'ouvre directement dans Odoo pour que vous puissiez agir (mettre a jour, annuler, verifier les details).'
            )}
          </p>
          <p className="text-xs text-[var(--ink-3)]">
            {t(lang,
              'The link goes to: lamptwist.odoo.com/odoo/sales/{order_id}',
              'Le lien pointe vers : lamptwist.odoo.com/odoo/sales/{order_id}'
            )}
          </p>
        </Section>

        {/* 8. Data Pipeline (technical) */}
        <Section title={t(lang, 'How Data Gets Updated (technical)', 'Comment les donnees sont mises a jour (technique)')}>
          <p className="text-sm text-[var(--ink-3)] mb-3">
            {t(lang,
              'The data in this app comes from an automated pipeline that syncs Odoo and Shopify data into our database.',
              'Les donnees de cette appli proviennent d\'un pipeline automatise qui synchronise les donnees Odoo et Shopify dans notre base de donnees.'
            )}
          </p>

          <h4 className="text-sm font-medium text-[var(--ink)] mb-2">
            {t(lang, 'Data sources', 'Sources de donnees')}
          </h4>
          <ul className="list-disc list-inside text-sm text-[var(--ink)] space-y-1 mb-4">
            <li><strong>Odoo</strong> — {t(lang, 'Orders, stock moves, invoices, customer data', 'Commandes, mouvements de stock, factures, donnees client')}</li>
            <li><strong>Shopify</strong> — {t(lang, 'Product images, variant IDs, marketplace order IDs', 'Images produits, IDs variantes, IDs commandes marketplace')}</li>
          </ul>

          <h4 className="text-sm font-medium text-[var(--ink)] mb-2">
            {t(lang, 'How it works', 'Comment ca marche')}
          </h4>
          <ol className="list-decimal list-inside text-sm text-[var(--ink)] space-y-1 mb-3">
            <li>{t(lang, 'Fetches orders and order lines from Odoo', 'Recupere les commandes et lignes de commande depuis Odoo')}</li>
            <li>{t(lang, 'Fetches warehouse stock moves and pickings', 'Recupere les mouvements de stock et pickings de l\'entrepot')}</li>
            <li>{t(lang, 'Computes item and order statuses based on what\'s happening in the warehouse', 'Calcule les statuts des articles et commandes selon ce qui se passe en entrepot')}</li>
            <li>{t(lang, 'Enriches with Shopify data (images, tracking)', 'Enrichit avec les donnees Shopify (images, suivi)')}</li>
            <li>{t(lang, 'Saves everything to the database', 'Sauvegarde tout dans la base de donnees')}</li>
          </ol>

          <TechNote lang={lang}>
            {t(lang,
              <>Two tables: <Tech>customer_order_zone</Tech> (line-level, 33 cols) and <Tech>customer_order_zone_agg</Tech> (order-level summary, 13 cols).
              Full sync: all orders since 2024-01-01. Refresh mode: incremental (changed + non-terminal orders from past year).
              Pipeline script: <Tech>fetch_customer_orders.py</Tech></>,
              <>Deux tables : <Tech>customer_order_zone</Tech> (niveau ligne, 33 cols) et <Tech>customer_order_zone_agg</Tech> (resume niveau commande, 13 cols).
              Sync complete : toutes les commandes depuis le 2024-01-01. Mode refresh : incremental (modifiees + non-terminales de l&apos;annee passee).
              Script pipeline : <Tech>fetch_customer_orders.py</Tech></>
            )}
          </TechNote>
        </Section>

        <div className="text-xs text-[var(--ink-3)] pt-4 pb-8 border-t border-[var(--line)]">
          {t(lang,
            'Last updated: March 2026',
            'Derniere mise a jour : mars 2026'
          )}
        </div>
      </div>
    </div></V2Shell>
  );
};

export default OrderDocs;
